import { z } from 'zod';
import {
  EventCents,
  EventDay,
  EventFlag,
  EventItemKey,
  EventKeyList,
  EventNoQuery,
  EventOptionalEnum,
  EventRowId,
  EventText,
  EventWhole,
} from './eventPlanningFields';

// Registration and outreach of an in-person event (/api/events/:id/registration): ticket types,
// outreach lists and the send log, comps and fee waivers, partners. Prices are whole cents; the
// note guard in the service refuses card numbers, SINs and banking details inside any text.
// The lists mirror the registration rules (the tests compare them).

export const EventOutreachKind = z.enum([
  'department_head',
  'training_director',
  'partner',
  'supervisor',
  'advertising_network',
  'outreach_target',
  'ambassador',
]);
export type EventOutreachKind = z.infer<typeof EventOutreachKind>;

export const EventSendKind = z.enum([
  'invitation',
  'reminder',
  'outreach',
  'calendar',
  'newsletter',
  'social',
  'other',
]);
export type EventSendKind = z.infer<typeof EventSendKind>;

export const EventSendChannel = z.enum(['own_email', 'platform']);
export type EventSendChannel = z.infer<typeof EventSendChannel>;

export const EventCompKind = z.enum(['comp_ticket', 'fee_waiver']);
export type EventCompKind = z.infer<typeof EventCompKind>;

export const EventCompReason = z.enum(['npi_course', 'partner', 'speaker_guest', 'hardship', 'staff', 'other']);
export type EventCompReason = z.infer<typeof EventCompReason>;

export const EventTicketFormat = z.enum(['in_person', 'virtual']);
export type EventTicketFormat = z.infer<typeof EventTicketFormat>;

export const EventPartnerMeal = z.enum(['breakfast', 'am_break', 'lunch', 'pm_break', 'dinner']);
export type EventPartnerMeal = z.infer<typeof EventPartnerMeal>;

export const EVENT_MAX_TICKET_TYPES = 8;

// ---------------------------------------------------------------- params and queries

export const EventRegistrationParams = z.strictObject({ id: EventRowId });
export const EventRegistrationListParams = z.strictObject({ id: EventRowId, listId: EventItemKey });
export const EventRegistrationSendParams = z.strictObject({ id: EventRowId, sendId: EventItemKey });
export const EventRegistrationCompParams = z.strictObject({ id: EventRowId, compId: EventItemKey });
export const EventRegistrationPartnerParams = z.strictObject({ id: EventRowId, partnerId: EventItemKey });
export const EventRegistrationNoQuery = EventNoQuery;

/** GET /api/events/:id/registration/calendar?audience= (unknown audiences are refused by the service). */
export const EventRegistrationCalendarQuery = z.strictObject({ audience: z.string().max(60).optional() });
export type EventRegistrationCalendarQuery = z.input<typeof EventRegistrationCalendarQuery>;

/** GET /api/events/:id/registration/lists/:listId/message?onBehalfOfName=&onBehalfOfEmail= */
export const EventOutreachMessageQuery = z.strictObject({
  onBehalfOfName: z.string().max(200).optional(),
  onBehalfOfEmail: z.string().max(320).optional(),
});
export type EventOutreachMessageQuery = z.input<typeof EventOutreachMessageQuery>;

// ---------------------------------------------------------------- requests

const AudienceKey = z.string().max(60).nullish();

export const EventTicketTypeInput = z
  .strictObject({
    key: z.string().max(60).nullish(),
    name: EventText(80),
    priceCents: EventCents,
    capacity: EventWhole,
    format: EventOptionalEnum(EventTicketFormat.options, 'A ticket is in person or virtual'),
    audienceKey: AudienceKey,
    opensOn: EventDay,
    closesOn: EventDay,
  })
  .partial();
export type EventTicketTypeInput = z.input<typeof EventTicketTypeInput>;

/** PUT /api/events/:id/registration/tickets; ticket types keep their keys. */
export const EventTicketsRequest = z
  .strictObject({
    salesOpen: EventDay,
    salesClose: EventDay,
    eventbriteUrl: EventText(500),
    types: z.array(EventTicketTypeInput).max(EVENT_MAX_TICKET_TYPES, `At most ${EVENT_MAX_TICKET_TYPES} ticket types`),
  })
  .partial();
export type EventTicketsRequest = z.input<typeof EventTicketsRequest>;

/** POST /api/events/:id/registration/lists (name and kinds, tags or contacts required by the service); PATCH …/:listId. */
export const EventOutreachListRequest = z
  .strictObject({
    name: EventText(100),
    kinds: z
      .array(z.enum(EventOutreachKind.options, { error: 'Unknown kind' }))
      .max(20)
      .nullish(),
    tags: z.union([z.array(z.string().max(200)).max(100), z.string().max(5000)]).nullish(),
    contactIds: EventKeyList.nullish(),
    excludeIds: EventKeyList.nullish(),
    audienceKey: AudienceKey,
    notes: EventText(1000),
  })
  .partial();
export type EventOutreachListRequest = z.input<typeof EventOutreachListRequest>;

/** POST /api/events/:id/registration/sends — a send made outside the platform, logged. */
export const EventSendLogRequest = z
  .strictObject({
    sentOn: EventDay,
    kind: EventOptionalEnum(EventSendKind.options, `What was sent: ${EventSendKind.options.join(', ')}`),
    audienceKey: AudienceKey,
    listId: z.string().max(200).nullish(),
    onBehalfOf: z
      .strictObject({ name: EventText(120), email: EventText(200) })
      .partial()
      .nullish(),
    channel: EventSendChannel.nullish(),
    recipients: EventWhole,
    subject: EventText(200),
    note: EventText(1000),
  })
  .partial();
export type EventSendLogRequest = z.input<typeof EventSendLogRequest>;

/** POST /api/events/:id/registration/comps (kind, reason, a person or a group); PATCH …/:compId, or { cancelled }. */
export const EventCompRequest = z
  .strictObject({
    kind: EventOptionalEnum(EventCompKind.options, 'A complimentary ticket or a fee waiver'),
    reason: z.enum(EventCompReason.options, { error: `Reason: ${EventCompReason.options.join(', ')}` }),
    inviteeId: z.string().max(200).nullish(),
    group: EventText(150),
    quantity: EventWhole,
    ticketTypeKey: z.string().max(60).nullish(),
    valueCents: EventCents,
    approvedBy: EventText(120),
    code: EventText(60),
    note: EventText(1000),
    cancelled: EventFlag,
  })
  .partial();
export type EventCompRequest = z.input<typeof EventCompRequest>;

/** POST /api/events/:id/registration/partners (name required by the service); PATCH …/:partnerId. */
export const EventPartnerRequest = z
  .strictObject({
    name: EventText(150),
    contactId: z.string().max(200).nullish(),
    sharedSessionIds: EventKeyList.nullish(),
    sharedMeals: z
      .array(
        z.strictObject({
          date: z.string().max(40),
          meal: z.enum(EventPartnerMeal.options, {
            error: `A shared meal is a day of the event and one of ${EventPartnerMeal.options.join(', ')}`,
          }),
        }),
      )
      .max(100)
      .nullish(),
    headcount: z.record(z.string().max(40), EventWhole).nullish(),
    delegates: z
      .strictObject({
        eventName: EventText(150),
        groupCode: EventText(60),
        perDelegateCents: EventCents,
        seatsReserved: EventWhole,
        registered: EventWhole,
        invoicedCents: EventCents,
        creditNoteCents: EventCents,
        vendorId: z.string().max(200).nullish(),
      })
      .partial()
      .nullish(),
    thinkTankSeats: EventWhole,
    notes: EventText(2000),
  })
  .partial();
export type EventPartnerRequest = z.input<typeof EventPartnerRequest>;
